import React, { useCallback, useEffect, useRef, useState } from 'react';
import enrichmentApi from '../services/enrichmentApi';
import icpApi from '../services/icpApi';
import Icon from './Icon';
import { industryLabel } from './utils';
import './RelationshipManagers.css';
import './IcpSegmentsTab.css';

const PAGE_SIZE = 50;
// How often a classify batch is checked on, matching ResearchButton
const POLL_MS = 3000;
const UNCLASSIFIED = 'unclassified';
const NOT_CHECKED = 'not-checked';
const SOURCE_LABELS = { 'icp-import': 'ICP workbook', auto: 'automatic', manual: 'by hand' };

const EMPTY_FORM = { name: '', rank: '', rationale: '', target_list: '', account_scoring: '' };

const toForm = (segment) => (segment ? {
  name: segment.name || '',
  rank: segment.rank ?? '',
  rationale: segment.rationale || '',
  target_list: segment.target_list || '',
  account_scoring: segment.account_scoring || ''
} : EMPTY_FORM);

// Empty strings clear a text field; a blank rank clears the rank
const fromForm = (form) => ({
  name: form.name.trim(),
  rank: form.rank === '' ? null : Number(form.rank),
  rationale: form.rationale.trim(),
  target_list: form.target_list.trim(),
  account_scoring: form.account_scoring.trim()
});

const companiesLabel = (count) => `${count.toLocaleString()} ${count === 1 ? 'company' : 'companies'}`;

// Why a segment can't be removed yet; the API refuses the same cases
const blockedReason = (segment) => {
  const reasons = [];
  if (segment.company_count) reasons.push(`${companiesLabel(segment.company_count)} in it, so move them first`);
  if (segment.listed_count) reasons.push(`the ICP workbook lists ${segment.listed_count} accounts under it`);
  return reasons.length ? `${reasons.join('; ')}.` : null;
};

const SegmentForm = ({ segment, onSaved, onCancel }) => {
  const [form, setForm] = useState(() => toForm(segment));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const nameRef = useRef(null);
  const id = (field) => `icp-form-${segment ? segment.id : 'new'}-${field}`;

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const update = (field) => (event) => setForm(current => ({ ...current, [field]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError('A name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const fields = fromForm(form);
      onSaved(segment
        ? await icpApi.updateSegment(segment.id, fields)
        : await icpApi.createSegment(fields));
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <form
      className="rm-form icp-form"
      onSubmit={submit}
      noValidate
      aria-label={segment ? `Edit ${segment.name}` : 'New ICP segment'}
    >
      <div className="rm-form__grid">
        <div className="rm-form__field">
          <label htmlFor={id('name')}>Name</label>
          <input id={id('name')} ref={nameRef} className="rm-input" value={form.name} onChange={update('name')}
                 maxLength={200} required aria-required="true" autoComplete="off" />
        </div>
        <div className="rm-form__field">
          <label htmlFor={id('rank')}>Rank</label>
          <input id={id('rank')} type="number" min="0" className="rm-input" value={form.rank} onChange={update('rank')}
                 autoComplete="off" />
        </div>
        <div className="rm-form__field rm-form__field--wide">
          <label htmlFor={id('rationale')}>Who belongs</label>
          <textarea id={id('rationale')} className="rm-input" rows={3} value={form.rationale}
                    onChange={update('rationale')} maxLength={4000}
                    aria-describedby={id('rationale-hint')} />
          <span id={id('rationale-hint')} className="rm-form__hint">
            Read by the classifier, so say what a company in this segment is, in plain words.
          </span>
        </div>
        <div className="rm-form__field rm-form__field--wide">
          <label htmlFor={id('account_scoring')}>How accounts are scored</label>
          <textarea id={id('account_scoring')} className="rm-input" rows={2} value={form.account_scoring}
                    onChange={update('account_scoring')} maxLength={4000} />
        </div>
        <div className="rm-form__field rm-form__field--wide">
          <label htmlFor={id('target_list')}>Target list</label>
          <textarea id={id('target_list')} className="rm-input" rows={2} value={form.target_list}
                    onChange={update('target_list')} maxLength={4000} />
        </div>
      </div>
      {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      <div className="rm-buttons">
        <button type="submit" className="rm-button rm-button--primary" disabled={saving}>
          {saving ? 'Saving...' : segment ? 'Save' : 'Add segment'}
        </button>
        <button type="button" className="rm-button" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </form>
  );
};

const SegmentRow = ({ segment, busy, error, onEdit, onDelete }) => {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const blocked = blockedReason(segment);

  return (
    <li className="rm-row icp-segment">
      <span className="icp-segment__rank" aria-hidden="true">{segment.rank ?? '–'}</span>
      <div className="rm-row__main">
        <div className="rm-row__name">{segment.name}</div>
        <div className="rm-row__meta">
          {companiesLabel(segment.company_count)} in it
          {segment.listed_count ? ` · ${segment.listed_count} on the ICP workbook's list` : ''}
        </div>
        {segment.rationale && <p className="rm-row__notes icp-segment__rationale" title={segment.rationale}>{segment.rationale}</p>}
        {blocked && !confirmingDelete && <p className="rm-row__meta">Can't be removed: {blocked}</p>}
        {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      </div>

      <div className="rm-row__side">
        {confirmingDelete ? (
          <div className="rm-row__actions" role="group" aria-label={`Remove ${segment.name}?`}>
            <span className="rm-row__meta">Remove it?</span>
            <button type="button" className="rm-button rm-button--danger" disabled={busy}
                    onClick={() => onDelete(segment)}>
              Remove
            </button>
            <button type="button" className="rm-button" disabled={busy} onClick={() => setConfirmingDelete(false)}>
              Keep
            </button>
          </div>
        ) : (
          <div className="rm-row__actions">
            <button type="button" className="rm-icon-button" onClick={() => onEdit(segment)} disabled={busy}
                    aria-label={`Edit ${segment.name}`} title="Edit">
              <Icon name="pencil" />
            </button>
            <button type="button" className="rm-icon-button rm-icon-button--danger"
                    onClick={() => setConfirmingDelete(true)} disabled={busy || Boolean(blocked)}
                    aria-label={`Remove ${segment.name}`} title={blocked ? "Can't be removed yet" : 'Remove'}>
              <Icon name="trash" />
            </button>
          </div>
        )}
      </div>
    </li>
  );
};

const SegmentsSection = ({ segments, onChanged }) => {
  // null, 'new', or the id of the segment being edited
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rowErrors, setRowErrors] = useState({});

  const finish = () => {
    setEditing(null);
    onChanged();
  };

  const remove = async (segment) => {
    setBusyId(segment.id);
    setRowErrors(current => ({ ...current, [segment.id]: null }));
    try {
      await icpApi.deleteSegment(segment.id);
      onChanged();
    } catch (err) {
      setRowErrors(current => ({ ...current, [segment.id]: err.message }));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="icp-section" aria-labelledby="icp-segments-title">
      <div className="icp-section__header">
        <h3 id="icp-segments-title" className="adm-heading">Segments</h3>
        {editing !== 'new' && (
          <button type="button" className="rm-button" onClick={() => setEditing('new')}>
            <Icon name="plus" /> Add a segment
          </button>
        )}
      </div>
      {editing === 'new' && <SegmentForm onSaved={finish} onCancel={() => setEditing(null)} />}
      {segments === null && <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>}
      <ul className="rm-list icp-segments">
        {(segments || []).map(segment => (
          editing === segment.id ? (
            <li key={segment.id} className="icp-segment-editing">
              <SegmentForm segment={segment} onSaved={finish} onCancel={() => setEditing(null)} />
            </li>
          ) : (
            <SegmentRow
              key={segment.id}
              segment={segment}
              busy={busyId === segment.id}
              error={rowErrors[segment.id]}
              onEdit={(chosen) => setEditing(chosen.id)}
              onDelete={remove}
            />
          )
        ))}
      </ul>
    </section>
  );
};

/**
 * Runs the classifier over the companies not yet checked, a batch at a time: each batch is followed until
 * the worker has finished it, then the next one starts, until the server has none left or Stop is pressed.
 * Stop only stops starting batches; what's already queued still gets done in the background.
 */
const ClassifySection = ({ notChecked, onChanged }) => {
  const [run, setRun] = useState(null);
  const [batches, setBatches] = useState(0);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const keepGoing = useRef(false);
  const rerunRef = useRef(false);
  // The last company id the previous batch covered: the next batch starts after it
  const cursor = useRef(0);

  const launch = useCallback(async (rerunMode) => {
    setError(null);
    try {
      const started = await icpApi.startClassifyRun({ rerun: rerunMode, afterId: cursor.current });
      cursor.current = started.last_company_id;
      setRun(started);
      setBatches(count => count + 1);
    } catch (err) {
      // 409: nothing is left, and the server says why
      keepGoing.current = false;
      setRun(null);
      if (err.status === 409) setMessage(`${err.message}.`);
      else setError(err.message);
    }
  }, []);

  const start = (rerunMode) => {
    keepGoing.current = true;
    rerunRef.current = rerunMode;
    cursor.current = 0;
    setBatches(0);
    setMessage(null);
    launch(rerunMode);
  };

  // Follow the batch in progress
  useEffect(() => {
    if (!run || run.status === 'done') return undefined;
    const timer = setTimeout(() => {
      enrichmentApi.getRun(run.id)
        .then(setRun)
        .catch((err) => {
          keepGoing.current = false;
          setRun(null);
          setError(err.message);
        });
    }, POLL_MS);
    return () => clearTimeout(timer);
  }, [run]);

  // A batch is finished: show the new numbers, then start the next one unless asked to stop
  useEffect(() => {
    if (!run || run.status !== 'done') return undefined;
    let current = true;
    Promise.resolve(onChanged()).finally(() => {
      if (!current) return;
      if (keepGoing.current) {
        launch(rerunRef.current);
      } else {
        setRun(null);
        setMessage('Stopped. The batch already under way finishes in the background.');
      }
    });
    return () => {
      current = false;
    };
  }, [run, launch, onChanged]);

  return (
    <section className="icp-section icp-classify" aria-labelledby="icp-classify-title">
      <h3 id="icp-classify-title" className="adm-heading">Classify companies</h3>
      <p className="rm-row__meta">
        The classifier reads each company and the segments' descriptions, and puts the company in the segment
        it fits, or in Unclassified when none does. A company set by hand, or from the workbook, is never changed.
      </p>
      {run ? (
        <div className="icp-classify__progress" role="status">
          <Icon name="loader" spin /> Batch {batches}: {run.done.toLocaleString()} of {run.total.toLocaleString()} companies
          sorted{run.failed ? `, ${run.failed} without an answer` : ''}.
          <button type="button" className="rm-button" onClick={() => { keepGoing.current = false; }}>
            Stop after this batch
          </button>
        </div>
      ) : (
        <div className="icp-classify__actions">
          <button
            type="button"
            className="rm-button rm-button--primary"
            onClick={() => start(false)}
            disabled={!notChecked}
          >
            {notChecked ? `Classify the ${companiesLabel(notChecked)} not yet checked` : 'Every company is checked'}
          </button>
          <button type="button" className="rm-link" onClick={() => start(true)}>
            Re-check the companies the classifier placed before
          </button>
        </div>
      )}
      {message && <p className="rm-row__meta" role="status">{message}</p>}
      {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
    </section>
  );
};

// What a company's classification reads as in the table: the segment, or its state
const SegmentChoice = ({ company, segments, busy, onChoose }) => {
  const current = company.icp_source === null ? '' : (company.icp_segment_id ? String(company.icp_segment_id) : UNCLASSIFIED);
  return (
    <select
      className="rm-input icp-table__select"
      value={current}
      disabled={busy}
      onChange={(event) => onChoose(company, event.target.value)}
      aria-label={`ICP segment of ${company.name}`}
    >
      {current === '' && <option value="" disabled>Not yet checked</option>}
      {segments.map(segment => (
        <option key={segment.id} value={String(segment.id)}>{segment.name}</option>
      ))}
      <option value={UNCLASSIFIED}>Unclassified</option>
    </select>
  );
};

const CompaniesSection = ({ segments, version, onMoved }) => {
  // 'segment:<id>', UNCLASSIFIED or NOT_CHECKED
  const [bucket, setBucket] = useState(segments.length ? `segment:${segments[0].id}` : UNCLASSIFIED);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busyCompanyId, setBusyCompanyId] = useState(null);
  const [rowErrors, setRowErrors] = useState({});

  // Search after a short pause in typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Reloads when the bucket, search or page changes, and when companies may have changed (version)
  useEffect(() => {
    let cancelled = false;
    const params = bucket === UNCLASSIFIED ? { unclassified: true }
      : bucket === NOT_CHECKED ? { not_checked: true }
        : { icp_segment_id: Number(bucket.slice('segment:'.length)) };
    icpApi.companiesIn({ ...params, q: search || undefined, page, limit: PAGE_SIZE })
      .then(data => {
        if (!cancelled) {
          setResult(data);
          setError(null);
        }
      })
      .catch(err => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [bucket, search, page, version]);

  const choose = async (company, value) => {
    setBusyCompanyId(company.id);
    setRowErrors(current => ({ ...current, [company.id]: null }));
    try {
      await icpApi.setCompanyIcp(company.id, value === UNCLASSIFIED ? null : Number(value));
      onMoved();
    } catch (err) {
      setRowErrors(current => ({ ...current, [company.id]: err.message }));
    } finally {
      setBusyCompanyId(null);
    }
  };

  const pagination = result?.pagination;
  const first = pagination && pagination.total_items ? (pagination.page - 1) * pagination.limit + 1 : 0;
  const last = pagination ? Math.min(pagination.page * pagination.limit, pagination.total_items) : 0;

  return (
    <section className="icp-section" aria-labelledby="icp-companies-title">
      <h3 id="icp-companies-title" className="adm-heading">Companies</h3>
      <p className="rm-row__meta">Change a company's segment here. A choice made here is kept, whatever the classifier or a later check says.</p>
      <div className="icp-companies__controls">
        <label className="icp-companies__bucket">
          <span>Show</span>
          <select
            className="rm-input"
            value={bucket}
            onChange={(event) => {
              setBucket(event.target.value);
              setPage(1);
            }}
          >
            {segments.map(segment => (
              <option key={segment.id} value={`segment:${segment.id}`}>In {segment.name}</option>
            ))}
            <option value={UNCLASSIFIED}>Unclassified</option>
            <option value={NOT_CHECKED}>Not yet checked</option>
          </select>
        </label>
        <input
          type="search"
          className="rm-input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Find a company"
          aria-label="Find a company by name"
          maxLength={200}
        />
      </div>

      {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      {!result && !error && <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>}
      {result && result.companies.length === 0 && <p className="placeholder-text">Nobody here.</p>}
      {result && result.companies.length > 0 && (
        <ul className="icp-table" aria-label="Companies">
          {result.companies.map(company => (
            <li key={company.id} className="icp-table__row">
              <div className="icp-table__name">
                <span>{company.name}</span>
                <span className="rm-row__meta">
                  {industryLabel(company.industry) || 'No industry'}
                  {company.on_target_list ? ' · on a target list' : ''}
                  {company.icp_source ? ` · ${SOURCE_LABELS[company.icp_source]}` : ''}
                </span>
                {rowErrors[company.id] && (
                  <span className="rm-error" role="alert"><Icon name="alert-triangle" /> {rowErrors[company.id]}</span>
                )}
              </div>
              <SegmentChoice company={company} segments={segments} busy={busyCompanyId === company.id} onChoose={choose} />
            </li>
          ))}
        </ul>
      )}

      {pagination && pagination.total_pages > 1 && (
        <div className="icp-pager">
          <button type="button" className="rm-button" disabled={!pagination.has_previous}
                  onClick={() => setPage(current => current - 1)}>
            <Icon name="chevron-left" /> Previous
          </button>
          <span className="rm-row__meta">{first}–{last} of {pagination.total_items.toLocaleString()}</span>
          <button type="button" className="rm-button" disabled={!pagination.has_next}
                  onClick={() => setPage(current => current + 1)}>
            Next <Icon name="chevron-right" />
          </button>
        </div>
      )}
    </section>
  );
};

const IcpSegmentsTab = () => {
  const [segments, setSegments] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // Bumped whenever companies may have changed, so the companies list reloads
  const [version, setVersion] = useState(0);

  const load = useCallback(async () => {
    try {
      const [list, stats] = await Promise.all([icpApi.listSegments(), icpApi.summary()]);
      setSegments(list.segments);
      setSummary(stats);
      setLoadError(null);
    } catch (err) {
      setLoadError(err.message);
    }
  }, []);

  // Reload the numbers and the companies list together
  const refresh = useCallback(async () => {
    setVersion(count => count + 1);
    await load();
  }, [load]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="adm-tab icp-tab">
      {loadError && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {loadError}</p>}
      {summary && (
        <div className="icp-stats" aria-label="ICP summary">
          <span className="icp-stat"><strong>{summary.segments.length}</strong> segments</span>
          <span className="icp-stat"><strong>{summary.unclassified_count.toLocaleString()}</strong> Unclassified</span>
          <span className="icp-stat"><strong>{summary.not_checked_count.toLocaleString()}</strong> not yet checked</span>
          <span className="icp-stat"><strong>{summary.manually_set_count.toLocaleString()}</strong> set by hand</span>
        </div>
      )}
      {summary && (
        <ClassifySection notChecked={summary.not_checked_count} onChanged={refresh} />
      )}
      {segments && (
        <SegmentsSection segments={segments} onChanged={refresh} />
      )}
      {segments && segments.length > 0 && (
        <CompaniesSection segments={segments} version={version} onMoved={refresh} />
      )}
    </div>
  );
};

export default IcpSegmentsTab;
